import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, X, Check, Loader2, ListChecks, Search } from 'lucide-react';
import { toast } from 'sonner';
import { bumpProfileOptions } from '@/hooks/useProfileOptions';

const API = '';

export default function AdminProfileOptionsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [editingValue, setEditingValue] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/profile-options`, { credentials: 'include' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setGroups(data);
      if (!activeKey && data.length > 0) setActiveKey(data[0].key);
    } catch {
      toast.error('Failed to load profile dropdowns');
    } finally {
      setLoading(false);
    }
  }, [activeKey]);

  useEffect(() => { load(); }, [load]);

  const active = groups.find(g => g.key === activeKey);

  const handleAdd = async () => {
    const v = newValue.trim();
    if (!v || !active) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/profile-options/${active.key}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: v }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Added "${v}"`);
      setNewValue('');
      setGroups(prev => prev.map(g => g.key === active.key ? { ...g, options: data.options } : g));
      bumpProfileOptions();
    } catch (e) { toast.error(e.message || 'Add failed'); }
    finally { setBusy(false); }
  };

  const handleRename = async (oldV) => {
    const newV = editingDraft.trim();
    if (!newV || newV === oldV) { setEditingValue(null); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/profile-options/${active.key}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: oldV, to: newV }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Renamed "${oldV}" → "${newV}"`);
      setEditingValue(null);
      setGroups(prev => prev.map(g => g.key === active.key ? { ...g, options: data.options } : g));
      bumpProfileOptions();
    } catch (e) { toast.error(e.message || 'Rename failed'); }
    finally { setBusy(false); }
  };

  const handleRemove = async (v) => {
    if (!active) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove "${v}" from ${active.label}?\n\nMembers who already selected it keep their tag, but it won't appear in the dropdown anymore.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/profile-options/${active.key}/${encodeURIComponent(v)}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Removed "${v}"`);
      setGroups(prev => prev.map(g => g.key === active.key ? { ...g, options: data.options } : g));
      bumpProfileOptions();
    } catch (e) { toast.error(e.message || 'Remove failed'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <Card className="p-8 text-center text-sm text-slate-500" data-testid="profile-options-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
      </Card>
    );
  }

  const filteredOpts = (active?.options || []).filter(o =>
    !search.trim() || o.toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="admin-profile-options-tab">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-brand-navy" /> Profile Dropdowns
        </h3>
        <p className="text-sm text-slate-500">
          Manage the options shown to members when they create or update their profile. Edits take effect for new selections immediately. Members who previously selected a removed option keep their existing tag.
        </p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        {/* LHS — dropdown chooser */}
        <Card className="p-2 divide-y" data-testid="profile-options-keys">
          {groups.map(g => (
            <button
              key={g.key}
              type="button"
              onClick={() => { setActiveKey(g.key); setEditingValue(null); setNewValue(''); setSearch(''); }}
              data-testid={`pick-key-${g.key}`}
              className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center justify-between ${
                activeKey === g.key
                  ? 'bg-brand-navy/5 text-brand-navy font-semibold'
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
            >
              <span className="truncate text-sm">{g.label}</span>
              <Badge variant="outline" className="text-[10px] flex-shrink-0">{g.options.length}</Badge>
            </button>
          ))}
        </Card>

        {/* RHS — options for active key */}
        {active && (
          <Card className="p-4 space-y-4" data-testid={`profile-options-detail-${active.key}`}>
            <div>
              <h4 className="text-base font-semibold text-slate-800">{active.label}</h4>
              <p className="text-xs text-slate-400">{active.options.length} option{active.options.length === 1 ? '' : 's'}</p>
            </div>

            {/* Add new */}
            <div className="flex items-center gap-2">
              <Input
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                placeholder={`Add a new ${active.label.toLowerCase()} option…`}
                maxLength={80}
                className="h-9"
                data-testid="add-option-input"
              />
              <Button onClick={handleAdd} disabled={busy || !newValue.trim()}
                data-testid="add-option-btn"
                className="bg-brand-navy hover:bg-brand-dark text-white h-9 flex-shrink-0">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Filter options…" className="pl-8 h-9" data-testid="filter-options-input" />
            </div>

            {/* List */}
            <div className="divide-y border border-slate-200 rounded-md max-h-[60vh] overflow-y-auto">
              {filteredOpts.length === 0 ? (
                <p className="px-3 py-6 text-sm text-slate-400 italic text-center">
                  {active.options.length === 0 ? 'No options yet — add the first one above.' : 'No matches.'}
                </p>
              ) : filteredOpts.map(opt => (
                <div key={opt} className="px-3 py-2 flex items-center gap-2"
                  data-testid={`option-row-${opt}`}>
                  {editingValue === opt ? (
                    <>
                      <Input value={editingDraft} onChange={e => setEditingDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRename(opt); if (e.key === 'Escape') setEditingValue(null); }}
                        maxLength={80} className="h-8 text-sm flex-1" autoFocus
                        data-testid="edit-option-input" />
                      <Button size="sm" onClick={() => handleRename(opt)} disabled={busy}
                        data-testid={`save-rename-${opt}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 w-8 p-0 flex-shrink-0">
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingValue(null)} disabled={busy}
                        data-testid={`cancel-rename-${opt}`}
                        className="h-8 w-8 p-0 flex-shrink-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-slate-800 flex-1 truncate">{opt}</span>
                      <Button size="sm" variant="ghost"
                        onClick={() => { setEditingValue(opt); setEditingDraft(opt); }}
                        data-testid={`rename-option-${opt}`}
                        className="h-8 w-8 p-0 text-slate-500 hover:text-brand-navy flex-shrink-0">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => handleRemove(opt)} disabled={busy}
                        data-testid={`remove-option-${opt}`}
                        className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50 flex-shrink-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
